import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import usePageTitle from '../../hooks/usePageTitle';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faStar, faBookmark, faDollarSign, faChartBar } from "@fortawesome/free-solid-svg-icons";
import DashboardShell from "../../components/Dashboard/DashboardShell";
import LoadingSpin from "../../components/Dashboard/LoadingSpin";
import { getFepksByFilmmakerId } from "../../api/epks";
import emptyBanner from "../../images/empty_banner.jpeg";

function EpkAnalyticsCard({ epk }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const banner = epk.banners?.[0]?.url || epk.banner_url;
  const bannerSrc = banner ? `${process.env.REACT_APP_AWS_URL}/${banner}` : emptyBanner;

  const stats = [
    { icon: faEye,        value: epk.viewCount ?? 0 },
    { icon: faStar,       value: epk.likes?.length ?? 0 },
    { icon: faBookmark,   value: epk.favourites?.length ?? 0 },
    { icon: faDollarSign, value: epk.wishes_to_buy?.length ?? 0 },
  ];

  return (
    <div
      onClick={() => navigate(`/dashboard/epk-analytics/${epk._id}`)}
      className="tw-group tw-relative tw-flex tw-flex-col tw-rounded-xl tw-border tw-border-white/10 tw-bg-[#280D41] tw-overflow-hidden tw-cursor-pointer hover:tw-border-[#712CB0]/60 tw-transition-colors"
    >
      <div className="tw-relative tw-overflow-hidden">
        <img
          src={bannerSrc}
          alt={epk.title}
          className="tw-w-full tw-h-36 tw-object-cover tw-transition-transform group-hover:tw-scale-105"
        />
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/60 tw-to-transparent tw-flex tw-items-end tw-p-3">
          <p className="tw-text-white tw-font-semibold tw-text-sm tw-leading-tight tw-line-clamp-2 tw-m-0">
            {epk.title || t("Untitled EPK")}
          </p>
        </div>
        <div className="tw-absolute tw-top-2 tw-right-2 tw-flex tw-items-center tw-justify-center tw-h-7 tw-w-7 tw-rounded-full tw-bg-black/40 tw-backdrop-blur-sm tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
          <FontAwesomeIcon icon={faChartBar} className="tw-text-white tw-text-xs" />
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-justify-around tw-px-3 tw-py-2.5 tw-border-t tw-border-white/10">
        {stats.map(({ icon, value }, i) => (
          <div key={i} className="tw-flex tw-flex-col tw-items-center tw-gap-0.5">
            <FontAwesomeIcon icon={icon} className="tw-text-[#712CB0] tw-text-sm" />
            <span className="tw-text-xs tw-font-semibold tw-text-white/60">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AnalyticsOverviewPage() {
  usePageTitle('Analytics');
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const [epkList, setEpkList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFepksByFilmmakerId(user.id).then((res) => {
      setEpkList(Array.isArray(res) ? res : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user.id]);

  return (
    <DashboardShell role={user.role} pageTitle={t("Analytics")}>
      <div className="tw-flex tw-flex-col tw-gap-6">
        <p className="tw-text-sm tw-text-white/50 tw-m-0">
          {t("Select an EPK to view its detailed analytics.")}
        </p>

        {loading ? (
          <LoadingSpin />
        ) : epkList.length === 0 ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-20">
            <FontAwesomeIcon icon={faChartBar} className="tw-text-white/20 tw-text-3xl" />
            <p className="tw-text-white/40 tw-text-sm tw-m-0">{t("No EPKs to show analytics for.")}</p>
          </div>
        ) : (
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4">
            {epkList.map((epk) => (
              <EpkAnalyticsCard key={epk._id} epk={epk} />
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
